import { siteConfig } from '@/mocks/data';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const brandPillars = [
  {
    icon: 'ri-brain-line',
    title: '心理學與自我理解',
    description: '透過心理學相關閱讀內容，協助讀者認識情緒、思考方式與自我經驗。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '情緒與人際關係',
    description: '涵蓋情緒理解、溝通、人際互動及關係議題等一般閱讀主題。',
  },
  {
    icon: 'ri-book-open-line',
    title: '大眾心理學',
    description: '以適合一般讀者閱讀的方式，呈現心理、行為與生活相關知識。',
  },
];

const brandIdentity = [
  { label: '正式名稱', value: '樂律文化' },
  { label: '英文名稱', value: 'LELYRIC' },
  { label: '出版定位', value: '心理學類書籍' },
];

export default function AboutPreview() {
  const section = useScrollReveal();

  return (
    <section
      className="w-full min-h-[90svh] flex items-center px-6 md:px-10 py-20 md:py-28 bg-gradient-to-b from-primary-50/70 via-background-50 to-background-50"
      ref={section.ref}
    >
      <div
        className={`w-full max-w-5xl mx-auto transition-all duration-700 ease-out ${
          section.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Section Label */}
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <span className="block w-5 h-px bg-primary-400/50"></span>
          <span className="text-[13px] font-medium text-primary-600 tracking-[0.15em] uppercase whitespace-nowrap">
            品牌資訊
          </span>
          <span className="block w-5 h-px bg-primary-400/50"></span>
        </div>

        {/* Title */}
        <div className="text-center mb-4">
          <h2 className="font-heading text-[2.25rem] md:text-[2.75rem] font-semibold text-foreground-950 leading-[1.15] tracking-tight">
            認識樂律文化
          </h2>
          <p className="mt-3 text-base text-foreground-500 font-light tracking-wide">
            心理理解、情緒與人際閱讀
          </p>
        </div>

        {/* Brand Identity Row */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md:gap-x-10 mt-9 mb-12">
          {brandIdentity.map((item, i) => (
            <span key={item.label} className="inline-flex items-center gap-2">
              {i > 0 && (
                <span className="hidden md:block w-px h-4 bg-foreground-200/70"></span>
              )}
              <span className="text-[13px] text-foreground-400 font-light">{item.label}</span>
              <span className="text-[14px] font-semibold text-foreground-900 tracking-wide">
                {item.value}
              </span>
            </span>
          ))}
        </div>

        {/* Brand Intro Paragraph */}
        <div className="max-w-2xl mx-auto mb-14">
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed font-light text-center">
            樂律文化以心理學類書籍為主要出版方向，內容涵蓋情緒、自我理解、人際關係與大眾心理學等閱讀主題，提供讀者從不同角度認識心理、行為與生活經驗。網站呈現樂律文化的品牌定位、2026 最新發行書籍與相關出版資訊。
          </p>
        </div>

        {/* Three Content Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {brandPillars.map((pillar) => (
            <div
              key={pillar.title}
              className="group p-7 md:p-8 rounded-lg bg-background-100/80 border border-background-200/50 hover:border-accent-200/60 transition-colors duration-300"
            >
              <div className="w-10 h-10 rounded-full bg-accent-100/80 flex items-center justify-center mb-5">
                <i className={`${pillar.icon} text-accent-600 text-lg`}></i>
              </div>
              <h3 className="font-heading text-[15px] font-semibold text-foreground-900 mb-3 leading-snug">
                {pillar.title}
              </h3>
              <p className="text-[14px] text-foreground-500 leading-relaxed">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* Brand Relationship */}
        <div className="max-w-2xl mx-auto mb-10 p-6 rounded-lg bg-background-100/60 border border-background-200/60">
          <p className="text-[14px] text-foreground-500 leading-relaxed text-center font-light tracking-wide">
            本品牌委託崧博出版經銷，官方網站由崧博出版製作。
          </p>
        </div>

        {/* Catalog Database CTA */}
        <div className="text-center">
          <p className="text-[14px] text-foreground-500 mb-5 font-light tracking-wide">
            完整書名、作者與出版資訊，請前往崧博書籍資料庫查詢。
          </p>
          <a
            href={siteConfig.catalogUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center gap-2.5 px-7 py-3 rounded-full text-[15px] font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer tracking-wide"
          >
            前往書籍資料庫
            <i className="ri-external-link-line text-[13px]"></i>
          </a>
        </div>
      </div>
    </section>
  );
}